import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    IconButton,
    FormHelperText,
    Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { convertGenderText } from '../utils/consts';

const SupportingThoughtsSection = ({ onNext, onBack, isFirstSection, formData }) => {
    const [newThought, setNewThought] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editedValue, setEditedValue] = useState('');
    const { handleSubmit, formState: { errors }, setValue, watch, reset } = useForm({
        defaultValues: {
            wspierajace_mysli_lista: formData?.wspierajace_mysli_lista || [],
            wspierajace_mysli_dodatkowe_mysli: formData?.wspierajace_mysli_dodatkowe_mysli || ''
        }
    });

    // Update form values when formData changes (when user goes back)
    useEffect(() => {
        if (formData?.wspierajace_mysli_lista) {
            reset({
                wspierajace_mysli_lista: formData?.wspierajace_mysli_lista || [],
                wspierajace_mysli_dodatkowe_mysli: formData?.wspierajace_mysli_dodatkowe_mysli || ''
            });
        }
    }, [formData, reset]);

    const thoughts = watch('wspierajace_mysli_lista');

    const handleAddThought = () => {
        if (newThought.trim() && thoughts.length < 3) {
            setValue('wspierajace_mysli_lista', [...thoughts, newThought.trim()]);
            setNewThought('');
        }
    };

    const handleRemoveThought = (index) => {
        setValue('wspierajace_mysli_lista', thoughts.filter((_, i) => i !== index));
    };

    const handleEditThought = (index) => {
        setEditingIndex(index);
        setEditedValue(thoughts[index]);
    };

    const handleSaveEdit = () => {
        if (editedValue.trim()) {
            const updatedThoughts = [...thoughts];
            updatedThoughts[editingIndex] = editedValue.trim();
            setValue('wspierajace_mysli_lista', updatedThoughts);
            setEditingIndex(null);
            setEditedValue('');
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditedValue('');
    };

    // const validateThoughts = (value) => {
    //     if (!value || value.length < 3) {
    //         return "Proszę wymienić minimum 3 wspierające myśli";
    //     }
    //     return true;
    // };

    return (
        <Box component="form" onSubmit={handleSubmit(onNext)} sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
                To myśli, które pomagają radzić sobie z trudnościami, wierzyć w siebie i dostrzegać możliwości.
                <br />
                <br />
                <b>Przykład:</b>
                <br />
                Zamiast: „Nie dam rady" → myśl: „Spróbuję, a jeśli się nie uda, znajdę inny sposób."
                <br />
                <br />
                <b>Dlaczego są ważne?</b>
                <br />
                <ul>
                    <li>Poprawiają nastrój i dają spokój.</li>
                    <li>Motywują do działania.</li>
                    <li>Budują pewność siebie.</li>
                </ul>
            </Typography>

            <Typography variant="h6" color="black" gutterBottom>
                {`Pomyśl o wcześniejszej sytuacji, w której sobie ${convertGenderText('poradziłeś/aś', formData.zwrot_plec)}. Wypisz 3 myśli, które wtedy Cię wspierały:`}
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        fullWidth
                        value={newThought}
                        onChange={(e) => setNewThought(e.target.value)}
                        placeholder="Wpisz wspierającą myśl..."
                        disabled={thoughts.length >= 3}
                    />
                    <Button
                        onClick={handleAddThought}
                        disabled={!newThought.trim() || thoughts.length >= 3}
                        color="primary"
                    >
                        Dodaj odpowiedź
                    </Button>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <List>
                        {thoughts.map((thought, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    editingIndex === index ? (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                edge="end"
                                                aria-label="save"
                                                onClick={handleSaveEdit}
                                                color="primary"
                                            >
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="cancel"
                                                onClick={handleCancelEdit}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                onClick={() => handleEditThought(index)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleRemoveThought(index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                }
                            >
                                {editingIndex === index ? (
                                    <TextField
                                        fullWidth
                                        value={editedValue}
                                        onChange={(e) => setEditedValue(e.target.value)}
                                        size="small"
                                        autoFocus
                                    />
                                ) : (
                                    `${index + 1}. ${thought}`
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Paper>

                {errors.wspierajace_mysli_lista && (
                    <FormHelperText error>
                        {errors.wspierajace_mysli_lista.message}
                    </FormHelperText>
                )}

                {(thoughts.length < 3 && thoughts.length > 0) && (
                    <Typography variant="body2" color="primary">
                        Co jeszcze?
                    </Typography>
                )}
            </Box>
            {/* 
            <Controller
                name="wspierajace_mysli_dodatkowe_mysli"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        label="Dodatkowe przemyślenia dotyczące Twoich wspierających myśli (opcjonalne)"
                        sx={{ mb: 3 }}
                    />
                )}
            /> */}

            <Box sx={{ display: 'flex', gap: 2 }}>
                <IconButton
                    onClick={onBack}
                    disabled={isFirstSection}
                    sx={{
                        border: '1px solid',
                        borderColor: isFirstSection ? 'action.disabled' : 'primary.main',
                        borderRadius: 1
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={thoughts.length < 3}
                    endIcon={<ArrowForwardIcon />}
                >
                    Dalej
                </Button>
            </Box>
        </Box>
    );
};

export default SupportingThoughtsSection; 